import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildAuthHeaders } from 'BackRoyalApi/queryShared';
import { useErrorLogService } from 'ErrorLogging';
import transformKeyCase from 'Utils/transformKeyCase';
import './locales/front_royal_upload/front_royal_upload-en.json';

// The old `ngf-max-size` values, using the codebase's MB convention (1024 * 1024, see
// maxSizeMBValidator). Each cap matches its S3 model's validates_attachment_size, so the client
// rejects an oversized file with a clear message instead of letting the server 422 it.
//
// 10MB covers English language proficiency documents, on the learner page and in the admin alike.
// 30MB is for transcripts and supplemental enrollment documents.
const MAX_UPLOAD_SIZE_MB = 10;
export const ADMIN_MAX_UPLOAD_SIZE_MB = 30;

// The accept list from the old ngf-select inputs. Every document input carried the same seven
// types; they differed only in the order of the image types, which `accept` ignores.
export const DOCUMENT_UPLOAD_ACCEPT =
    'application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpg,image/png,image/jpeg,image/webp';

// Shown when the server rejects the file type, matching frontRoyalUpload's message.
const SUPPORTED_FORMATS_FOR_ERROR_MESSAGE = '.pdf, .doc, .docx, .jpg, .png';

type UploadResponse = { contents?: Record<string, Record<string, unknown>[]> };

type UploadArgs = {
    url: string;
    file: File;
    recordFields?: Record<string, string>;
    // Booleans are allowed: FormData stringifies them, and Rails casts 'true'/'false' back.
    topLevelFields?: Record<string, string | boolean>;
    responseKey: string;
};

// `unsupportedType` is the server's 406. `failed` covers a network error or any other non-2xx.
// `noRecord` means a 2xx that carried nothing usable.
type UploadFailure = { failure: 'unsupportedType' | 'failed' | 'noRecord'; status?: number; cause?: unknown };
type UploadResult<T> = { record: T } | UploadFailure;

function isFailure<T>(result: UploadResult<T>): result is UploadFailure {
    return 'failure' in result;
}

// Every S3 endpoint uses `params.expect(record: [...])`, so the file and its siblings must be
// nested under `record[...]`; only the English-document endpoint reads extra fields from the top
// level.
function buildFormData({ file, recordFields = {}, topLevelFields = {} }: UploadArgs) {
    const formData = new FormData();
    formData.append('record[file]', file);
    Object.entries(recordFields).forEach(([key, value]) => formData.append(`record[${key}]`, value));
    Object.entries(topLevelFields).forEach(([key, value]) => formData.append(key, String(value)));
    return formData;
}

async function submitUpload<T>(args: UploadArgs): Promise<UploadResult<T>> {
    try {
        const response = await fetch(args.url, {
            method: 'POST',
            headers: buildAuthHeaders(),
            body: buildFormData(args),
        });

        // fetch only rejects on network errors, so a non-2xx would otherwise fall through silently.
        // 406 is how the server reports an unacceptable file (see frontRoyalUpload).
        if (!response.ok) {
            const failure = response.status === 406 ? 'unsupportedType' : 'failed';
            return { failure, status: response.status };
        }

        const data = (await response.json()) as UploadResponse;
        const record = data?.contents?.[args.responseKey]?.[0];
        if (!record) return { failure: 'noRecord' };

        // The response is snake_cased; everything downstream reads the camelCased shape.
        return { record: transformKeyCase<T>(record, { to: 'camelCase' }) };
    } catch (cause) {
        return { failure: 'failed', cause };
    }
}

// Uploads a single document via multipart form data, replacing frontRoyalUpload/ng-file-upload for
// the React document UIs. Follows the same fetch + buildAuthHeaders approach as
// edit_account/useAvatarUpload.ts.
export function useUploadDocument({ maxSizeMb = MAX_UPLOAD_SIZE_MB }: { maxSizeMb?: number } = {}) {
    const { t } = useTranslation('back_royal');
    const ErrorLogService = useErrorLogService();
    const [uploading, setUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const reportFailure = useCallback(
        (result: UploadFailure, args: UploadArgs) => {
            if (result.failure === 'unsupportedType') {
                setErrorMessage(
                    t('front_royal_upload.front_royal_upload.unsupported_file_type', {
                        allowedTypes: SUPPORTED_FORMATS_FOR_ERROR_MESSAGE,
                    }),
                );
                return;
            }

            setErrorMessage(t('front_royal_upload.front_royal_upload.unknown_file_issue'));
            const message =
                result.failure === 'noRecord' ? 'Document upload returned no record' : 'Document upload failed';
            ErrorLogService.notify(message, result.cause ?? null, {
                status: result.status,
                url: args.url,
                response_key: args.responseKey,
            });
        },
        [t, ErrorLogService],
    );

    const upload = useCallback(
        async <T>(args: UploadArgs): Promise<T | null> => {
            setErrorMessage(null);

            if (args.file.size > maxSizeMb * 1024 * 1024) {
                setErrorMessage(t('front_royal_upload.front_royal_upload.max_file_size', { size: `${maxSizeMb}MB` }));
                return null;
            }

            setUploading(true);
            try {
                const result = await submitUpload<T>(args);
                if (isFailure(result)) {
                    reportFailure(result, args);
                    return null;
                }
                return result.record;
            } finally {
                setUploading(false);
            }
        },
        [t, maxSizeMb, reportFailure],
    );

    return { upload, uploading, errorMessage };
}
